import { Input } from "@/shadcn/components/ui/input";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useAuth, useFeedbackTicketDesktop, useI18N } from "common/hooks";
import { useEffect, useRef, useState } from "react";
import { readFile } from "@/helpers/cropImage";
import { XIcon } from "lucide-react";
import Zoom from "react-medium-image-zoom";
import { AddPhotoImageData } from "common/types";
import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import { useGetInTouchModal } from "@/hooks/useGetInTouchModal";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";
import { Storage } from "common/storage";
import AnalyticsService from "@/services/analytics";

export function GetInTouchModal() {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [notes, setNotes] = useState("");
  const { user } = useAuth();
  const showToast = useCustomToast();
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);

  const [errors, setErrors] = useState({
    note: false,
  });
  const noteRef = useRef(null);

  const { mutate, error, isPending, data } = useFeedbackTicketDesktop();

  const { isGetInTouchModalVisible, hideGetInTouchModal } =
    useGetInTouchModal();

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("appFeedbackErrorSubmit");
      AnalyticsService.trackMx("appFeedbackErrorSubmit", {});

      showToast(error?.message, "error");
    }
  }, [isDark, error]);

  useEffect(() => {
    if (data && data.data.feedback === "saved") {
      showToast(data?.message, "success");
      setAttachedImages([]);
      setNotes("");
      setErrors({
        note: false,
      });
      hideGetInTouchModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDark]);

  const validateFields = () => {
    const newErrors = {
      note: notes.length < 5,
    };

    setErrors(newErrors);

    const isNoteValid = !newErrors.note;

    if (!isNoteValid) {
      noteRef.current.scrollIntoView({ behavior: "smooth" });
    }

    return isNoteValid;
  };

  const submit = async () => {
    if (!validateFields()) {
      return;
    }
    // console.log("inside submit");
    if (user) {
      AnalyticsService.trackGAEventHandler("appFeedbackSubmit");
      AnalyticsService.trackMx("appFeedbackSubmit", {});

      mutate({
        accessToken: Storage.getCurrentUserToken(),
        notes,
        images: attachedImages,
      });
    } else {
      showToast(i18n.get("mustBeLoggedInToSubmitReview"), "error");
    }
  };

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isGetInTouchModalVisible}
      onClose={() => hideGetInTouchModal()}
      classNames={{
        modal:
          "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
        closeButton: "hidden",
      }}
    >
      {isPending && <Loader />}
      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-150px)] md:h-[calc(100vh-220px)] overflow-y-auto"
        role="none"
      >
        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 vsticky top-0">
          <h1
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            tabIndex={0}
            ref={firstFocusRef} // Attach the ref here
          >
            {i18n.get("getInTouch")}
          </h1>
        </header>

        <div
          aria-label="Modal content"
          className="flex-grow overflow-y-auto py-3 px-3"
          role="none"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <h2
                className="self-stretch text-primary-800 dark:text-primary-150 desktop-h4-medium-bold"
                tabIndex={0}
              >
                {i18n.get("getInTouch")}
              </h2>
              <div
                className="self-stretch text-primary-800 dark:text-primary-150 paragraph-small-semibold md:desktop-h4-medium-semi-bold"
                tabIndex={0}
              >
                {i18n.get("letUsKnowYourFeedback")}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="w-full">
                <label htmlFor="review" className="sr-only">
                  {i18n.get("getInTouchPlaceholderText")}
                </label>
                <Textarea
                  id="review"
                  placeholder={i18n.get("getInTouchPlaceholderText")}
                  className="scrollbar-thin dark:scrollbar-thin-dark h-28"
                  value={notes}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                />
                <div
                  id="charCount"
                  className="button-small-medium text-neutral-500 mt-3"
                  tabIndex={0}
                >
                  <span className={notes.length < 5 ? "text-red-600" : ""}>
                    {notes.length}
                  </span>
                  /500 Characters - at least 5 characters required
                </div>
                {errors.note && (
                  <div className="text-red-600" role="alert" tabIndex={0}>
                    {i18n.get("getInTouchPlaceholderText")}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full" tabIndex={0}>
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto">
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt={`Attached image ${index + 1}`}
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <button
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index
                            )
                          );
                        }}
                        className="absolute top-2 right-3 m-2"
                        aria-label={`Remove attached image ${index + 1}`}
                      >
                        <div className="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex">
                          <div className="w-4 h-4 relative flex-col justify-start items-start flex">
                            <img
                              src="/images/Close.svg"
                              alt="Remove attached image"
                              className="w-4 h-4"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900 z-1000">
          <div className="flex-col justify-end items-center gap-4 flex w-[90%] md:w-full md:max-w-[70%]">
            <div
              className="w-[327px] justify-start items-center gap-4 inline-flex"
              tabIndex={0}
            >
              <label
                htmlFor="picture"
                className="grow shrink basis-0 h-12 px-6 py-3 bg-primary-50 dark:bg-dark-800 rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                aria-label="Add photos"
              >
                <div className="text-center text-primary-800 dark:text-primary-150 mobile-h3-small-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                  aria-label="Upload photos"
                />
              </label>
            </div>

            <div className="w-[327px] justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={notes.length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit feedback"
              >
                {i18n.get("submit")}
              </WideButton>
            </div>
          </div>
        </footer>
        <button
          onClick={() => {
            setAttachedImages([]);
            setNotes("");
            hideGetInTouchModal();
          }}
          className="absolute top-4 left-4"
          aria-label="Close the get in touch form"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </section>
    </Modal>
  );
}
